import React, { Fragment, useState } from 'react';
import { PageTitle } from '../../../components/PageTitle';
import { useTranslation } from 'react-i18next';

import { useLocation, useNavigate } from 'react-router';
import { FloatingActionsButtons } from '../../../components/FloatingActionsButtons';
import { CreateAction } from '../../../components/FloatingActionsButtons/components/CreateAction';
import { UpsertClientDialog } from '../dialogs/UpsertClientDialog';
import { ManagedDataTable } from '../../../components/ManagedDataTable';
import { useGetAreasList, useGetClients } from '../../../reactQuery/clients';
import {
  ColumnFilters,
  IManagedDataTable,
} from '../../../components/ManagedDataTable/interfaces';
import { Client, Status } from '../../../api/clientsApi/interfaces';
import { columns } from './helpers';
import { DATA_TABLE_STORAGE_KEYS } from '../../../constants';
import { useGetGroupList } from '../../../reactQuery/groups';
import { useGetIslandsList } from '../../../reactQuery/islands';
import { useGetLeafSectorList } from '../../../reactQuery/sectors';
import { ROUTES } from '../../../routes/Routes';
import { useGetAgentsList } from '../../../reactQuery/Agents/agents';

export const ClientsOverview: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);

  const { data: groupList } = useGetGroupList();
  const { data: islandList } = useGetIslandsList();
  const { data: areaList } = useGetAreasList();
  const { data: leafSectorList } = useGetLeafSectorList();
  const { data: agentsList } = useGetAgentsList();

  const onRowClick = (row: Client): void => {
    navigate(ROUTES.clients.details(row.id), { state: { from: location } });
  };

  const columnFilters: ColumnFilters = [
    {
      column: 'group',
      label: t('entities:client.group'),
      type: 'multivalue',
      options: groupList || [],
    },
    {
      column: 'island',
      label: t('entities:client.island'),
      type: 'multivalue',
      options: islandList || [],
    },
    {
      column: 'area',
      label: t('entities:client.area'),
      type: 'multivalue',
      options: areaList || [],
    },
    {
      column: 'sector',
      label: t('entities:client.sector'),
      type: 'multivalue',
      options: leafSectorList || [],
    },
    {
      column: 'status',
      label: t('entities:client.status'),
      type: 'multivalue',
      options: Object.values(Status).map((statusKey) => ({
        id: statusKey,
        name: t(`entities:client.enums.status.${statusKey}`),
      })),
    },
    {
      column: 'responsibleAgents',
      label: t('entities:client.responsibleAgents'),
      type: 'multivalue',
      options:
        agentsList?.map((agent) => {
          return {
            id: agent.id,
            name: `${agent.firstName} ${agent.lastName}`,
          };
        }) || [],
    },
    {
      column: 'createdBy',
      label: t('entities:common.createdBy'),
      type: 'multivalue',
      options:
        agentsList?.map((agent) => {
          return {
            id: agent.id,
            name: `${agent.firstName} ${agent.lastName}`,
          };
        }) || [],
    },
  ];

  return (
    <Fragment>
      <PageTitle
        title={t('clients:title')}
        subTitle={t('clients:subTitle')}
        space
      />
      <ManagedDataTable
        query={useGetClients as IManagedDataTable<Client>['query']}
        columns={columns}
        onRowClick={onRowClick}
        storageKey={DATA_TABLE_STORAGE_KEYS.clients}
        initialSort={{ field: 'name', sort: 'asc' }}
        columnFilters={columnFilters}
      />
      <UpsertClientDialog
        show={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
        }}
      />
      <FloatingActionsButtons>
        <CreateAction onClick={() => setShowCreateDialog(true)} />
      </FloatingActionsButtons>
    </Fragment>
  );
};
